import React, { useState, useEffect, useCallback, useMemo } from "react";
import { roleService } from "../../../service/admin/roleService";
import { FaPlus, FaEdit, FaTrash, FaUsers, FaCheck, FaTimes, FaUserShield, FaKey, FaSync, FaSearch, FaStore, FaShieldAlt } from "react-icons/fa";
import ExcelTable from "../../../component/library/excelTable";
import Pagination from "../../../component/library/pagination";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import "./style.css";

// Menu Tooltip Component
const MenusTooltip = ({ menus, count }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [position, setPosition] = React.useState({ top: 0, left: 0 });
  const wrapperRef = React.useRef(null);
  
  const handleMouseEnter = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top + window.scrollY,
        left: rect.left + rect.width / 2 + window.scrollX
      });
    }
    // Debug: Log the menus data
    console.log('Menus data:', menus);
    setShowTooltip(true);
  };
  
  return (
    <div 
      ref={wrapperRef}
      className="role-menus-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span className="role-permissions-badge" style={{ cursor: 'pointer' }}>
        {count || 0} menus
      </span>
      {showTooltip && (
        <div 
          className="role-menus-tooltip" 
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: 'translate(-50%, calc(-100% - 12px))',
            zIndex: 9999
          }}
        >
          <div className="role-tooltip-header">📋 Assigned Menus ({count || 0})</div>
          {menus && menus.length > 0 ? (
            <div className="role-tooltip-menus">
              {menus.map((menu, index) => (
                <span key={index} className="role-menu-chip">
                  <FaShieldAlt className="role-menu-chip-icon" />
                  {menu.menuName || menu.name || JSON.stringify(menu)}
                </span>
              ))}
            </div>
          ) : (
            <div className="role-tooltip-empty">❌ No menus assigned</div>
          )}
        </div>
      )}
    </div>
  );
};

export default function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [availableMenus, setAvailableMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isSubAdminModalOpen, setIsSubAdminModalOpen] = useState(false);
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, roleId: null });
  const [roleUsers, setRoleUsers] = useState([]);
  const [subAdmins, setSubAdmins] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [activeTab, setActiveTab] = useState("roles");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Sellers pagination and search
  const [sellersPage, setSellersPage] = useState(1);
  const [sellersLimit, setSellersLimit] = useState(10);
  const [sellersSearch, setSellersSearch] = useState("");
  const [sellersPagination, setSellersPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });

  // Form states
  const [roleForm, setRoleForm] = useState({ name: "", displayName: "", description: "", permissions: [] });
  const [subAdminForm, setSubAdminForm] = useState({ email: "", password: "", firstName: "", lastName: "", roleId: "" });

  // Get current user ID for per-user table settings
  const authData = getAuthLocalStorage();
  const currentUserId = authData?.loginHeader || null;

  const fetchRoles = useCallback(async () => {
    try {
      const response = await roleService.getAllRoles(true);
      if (response?.status?.code === 200) {
        setRoles(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  }, []);

  const fetchMenus = useCallback(async () => {
    try {
      const response = await roleService.getAvailableMenus();
      if (response?.status?.code === 200) {
        setAvailableMenus(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching menus:", error);
    }
  }, []);

  const fetchSubAdmins = useCallback(async () => {
    try {
      const response = await roleService.getSubAdmins();
      if (response?.status?.code === 200) {
        setSubAdmins(response.data?.users || []);
      }
    } catch (error) {
      console.error("Error fetching sub-admins:", error);
    }
  }, []);

  const fetchSellers = useCallback(async (page, limit, search) => {
    try {
      const params = {
        page: page || sellersPage,
        limit: limit || sellersLimit,
        ...(search !== undefined ? { search } : (sellersSearch ? { search: sellersSearch } : {})),
      };
      const response = await roleService.getSellers(params);
      if (response?.status?.code === 200) {
        setSellers(response.data?.users || []);
        setSellersPagination(response.data?.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          pages: 1,
        });
      }
    } catch (error) {
      console.error("Error fetching sellers:", error);
    }
  }, [sellersPage, sellersLimit, sellersSearch]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRoles(), fetchMenus(), fetchSubAdmins()]);
      setLoading(false);
    };
    loadData();
  }, [fetchRoles, fetchMenus, fetchSubAdmins]);

  // Fetch sellers when tab is active or pagination changes (not on search text change - only on button click)
  useEffect(() => {
    if (activeTab === "sellers") {
      setLoading(true);
      fetchSellers(sellersPage, sellersLimit, sellersSearch).finally(() => setLoading(false));
    }
  }, [activeTab, sellersPage, sellersLimit]);

  const handleCreateRole = () => {
    setSelectedRole(null);
    setRoleForm({ name: "", displayName: "", description: "", permissions: [] });
    setIsRoleModalOpen(true);
  };

  const handleEditRole = (role, e) => {
    e?.stopPropagation();
    setSelectedRole(role);
    setRoleForm({
      name: role.name,
      displayName: role.displayName,
      description: role.description || "",
      permissions: role.permissions || [],
    });
    setIsRoleModalOpen(true);
  };

  const handleSaveRole = async () => {
    try {
      if (selectedRole) {
        await roleService.updateRole(selectedRole._id, roleForm);
      } else {
        await roleService.createRole(roleForm);
      }
      setIsRoleModalOpen(false);
      fetchRoles();
    } catch (error) {
      console.error("Error saving role:", error);
      alert(error.message || "Failed to save role");
    }
  };

  const handleDeleteRole = async () => {
    try {
      await roleService.deleteRole(confirmModal.roleId);
      setConfirmModal({ isOpen: false, roleId: null });
      fetchRoles();
    } catch (error) {
      console.error("Error deleting role:", error);
      alert(error.message || "Failed to delete role");
    }
  };

  const handleViewUsers = async (role, e) => {
    e?.stopPropagation();
    try {
      const response = await roleService.getUsersByRole(role._id);
      if (response?.status?.code === 200) {
        setRoleUsers(response.data || []);
        setSelectedRole(role);
        setIsUsersModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleCreateSubAdmin = () => {
    setSubAdminForm({ email: "", password: "", firstName: "", lastName: "", roleId: roles[0]?._id || "" });
    setIsSubAdminModalOpen(true);
  };

  const handleSaveSubAdmin = async () => {
    try {
      await roleService.createSubAdmin(subAdminForm);
      setIsSubAdminModalOpen(false);
      fetchSubAdmins();
    } catch (error) {
      console.error("Error creating sub-admin:", error);
      alert(error.message || "Failed to create sub-admin");
    }
  };

  const toggleMenuPermission = (menuKey, field) => {
    setRoleForm(prev => {
      const existingIndex = prev.permissions.findIndex(p => p.menuKey === menuKey);
      const menu = availableMenus.find(m => m.menuKey === menuKey);
      
      if (existingIndex >= 0) {
        const updated = [...prev.permissions];
        if (field === 'canView' && updated[existingIndex].canView) {
          updated.splice(existingIndex, 1);
        } else {
          updated[existingIndex] = { ...updated[existingIndex], [field]: !updated[existingIndex][field] };
        }
        return { ...prev, permissions: updated };
      } else if (field === 'canView') {
        return {
          ...prev,
          permissions: [...prev.permissions, { ...menu, canView: true, canCreate: false, canEdit: false, canDelete: false }],
        };
      }
      return prev;
    });
  };

  const getPermission = (menuKey, field) => {
    const perm = roleForm.permissions.find(p => p.menuKey === menuKey);
    return perm ? perm[field] : false;
  };

  // Build display rows: Products group (Product List, Product Drafts), Failed Orders group (BTPL, Reseller, Other Sellers)
  const menuPermissionRows = useMemo(() => {
    const rows = [];
    let productGroupAdded = false;
    let failedOrdersGroupAdded = false;
    let chinaUpdateGroupAdded = false;
    availableMenus.forEach((menu) => {
      if (menu.menuKey === "product-list") {
        if (!productGroupAdded) {
          rows.push({ type: "group", key: "products-group", label: "Products" });
          productGroupAdded = true;
        }
        rows.push({ type: "menu", menu, indent: true });
      } else if (menu.menuKey === "product-drafts") {
        if (!productGroupAdded) {
          rows.push({ type: "group", key: "products-group", label: "Products" });
          productGroupAdded = true;
        }
        rows.push({ type: "menu", menu, indent: true });
      } else if (menu.menuKey === "product-errors") {
        if (!productGroupAdded) {
          rows.push({ type: "group", key: "products-group", label: "Products" });
          productGroupAdded = true;
        }
        rows.push({ type: "menu", menu, indent: true });
      } else if (menu.menuKey === "china-order-details" || menu.menuKey === "postage-to-india" || menu.menuKey === "in-comments") {
        if (!chinaUpdateGroupAdded) {
          rows.push({ type: "group", key: "china-update-group", label: "China Update" });
          chinaUpdateGroupAdded = true;
        }
        rows.push({ type: "menu", menu, indent: true });
      } else if (menu.menuKey === "failed-orders-btpl" || menu.menuKey === "failed-orders-reseller" || menu.menuKey === "failed-orders-other") {
        if (!failedOrdersGroupAdded) {
          rows.push({ type: "group", key: "failed-orders-group", label: "Failed Orders" });
          failedOrdersGroupAdded = true;
        }
        rows.push({ type: "menu", menu, indent: true });
      } else {
        rows.push({ type: "menu", menu, indent: false });
      }
    });
    return rows;
  }, [availableMenus]);

  const handleRefresh = () => {
    setLoading(true);
    if (activeTab === "sellers") {
      fetchSellers(sellersPage, sellersLimit, sellersSearch).finally(() => setLoading(false));
    } else {
      Promise.all([fetchRoles(), fetchSubAdmins()]).finally(() => setLoading(false));
    }
  };

  // Handle sellers search
  const handleSellersSearch = () => {
    setSellersPage(1);
    setLoading(true);
    fetchSellers(1, sellersLimit, sellersSearch).finally(() => setLoading(false));
  };

  const handleSellersSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSellersSearch();
    }
  };

  // Roles table data
  const rolesTableData = useMemo(() => 
    (roles || []).map((item, index) => ({
      ...item,
      serialNumber: index + 1,
    })),
    [roles]
  );

  // Sub-admins table data
  const subAdminsTableData = useMemo(() => 
    (subAdmins || []).map((item, index) => ({
      ...item,
      serialNumber: index + 1,
    })),
    [subAdmins]
  );

  // Sellers table data with user ID
  const sellersTableData = useMemo(() => 
    (sellers || []).map((item) => ({
      ...item,
      uniqueId: item.uniqueId || item._id || "-",
    })),
    [sellers]
  );

  // Roles columns
  const rolesColumns = [
    {
      key: "serialNumber",
      label: "S.No.",
      width: 70,
      render: (value) => <span className="role-serial">{value}</span>,
    },
    {
      key: "name",
      label: "Role Name",
      width: 160,
      render: (value, row) => (
        <div className="role-name-cell">
          <span className="role-name">{value}</span>
          {row.isSystem && <span className="role-system-badge">System</span>}
        </div>
      ),
    },
    {
      key: "displayName",
      label: "Display Name",
      width: 160,
      render: (value) => <span className="role-display-name">{value || "-"}</span>,
    },
    {
      key: "description",
      label: "Description",
      width: 200,
      render: (value) => <span className="role-description">{value || "-"}</span>,
    },
    {
      key: "permissions",
      label: "Permissions",
      width: 120,
      render: (value) => <MenusTooltip menus={value} count={value?.length} />,
    },
    {
      key: "isActive",
      label: "Status",
      width: 100,
      render: (value) => (
        <span className={`role-status-badge ${value ? 'role-status-active' : 'role-status-inactive'}`}>
          {value ? <><FaCheck /> Active</> : <><FaTimes /> Inactive</>}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      width: 120,
      render: (_, row) => (
        <div className="role-actions-cell">
          <button className="role-action-btn role-action-users" onClick={(e) => handleViewUsers(row, e)} title="View Users">
            <FaUsers />
          </button>
          <button 
            className="role-action-btn role-action-edit" 
            onClick={(e) => handleEditRole(row, e)} 
            title="Edit"
            disabled={row.isSystem && row.name === 'admin'}
          >
            <FaEdit />
          </button>
          {!row.isSystem && (
            <button 
              className="role-action-btn role-action-delete" 
              onClick={(e) => { e.stopPropagation(); setConfirmModal({ isOpen: true, roleId: row._id }); }} 
              title="Delete"
            >
              <FaTrash />
            </button>
          )}
        </div>
      ),
    },
  ];

  // Sub-admins columns
  const subAdminsColumns = [
    {
      key: "serialNumber",
      label: "S.No.",
      width: 70,
      render: (value) => <span className="role-serial">{value}</span>,
    },
    {
      key: "firstName",
      label: "Name",
      width: 180,
      render: (_, row) => (
        <div className="role-user-cell">
          <div className="role-user-avatar">
            {(row.firstName?.[0] || "U").toUpperCase()}
          </div>
          <span className="role-user-name">{row.firstName} {row.lastName}</span>
        </div>
      ),
    },
    {
      key: "email",
      label: "Email",
      width: 220,
      render: (value) => <span className="role-email">{value || "-"}</span>,
    },
    {
      key: "roleId",
      label: "Role",
      width: 140,
      render: (value, row) => (
        <span className={`role-badge ${row.role === 'admin' ? 'role-badge-admin' : 'role-badge-subadmin'}`}>
          {value?.displayName || row.role}
        </span>
      ),
    },
    {
      key: "assignedMenus",
      label: "Assigned Menus",
      width: 130,
      render: (value) => <MenusTooltip menus={value} count={value?.length} />,
    },
    {
      key: "isDisable",
      label: "Status",
      width: 100,
      render: (value) => (
        <span className={`role-status-badge ${value ? 'role-status-inactive' : 'role-status-active'}`}>
          {value ? "Disabled" : "Active"}
        </span>
      ),
    },
    {
      key: "lastLoginDate",
      label: "Last Login",
      width: 130,
      render: (value) => value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "-",
    },
    {
      key: "actions",
      label: "Assign Role",
      width: 160,
      render: (_, row) => (
        <select
          className="role-assign-select"
          value={row.roleId?._id || ""}
          onClick={(e) => e.stopPropagation()}
          onChange={async (e) => {
            if (e.target.value) {
              try {
                await roleService.assignRoleToUser(row._id, e.target.value);
                fetchSubAdmins();
                alert("Role assigned successfully! User needs to re-login to see updated permissions.");
              } catch (err) {
                alert(err.message || "Failed to assign role");
              }
            }
          }}
        >
          <option value="">Select Role</option>
          {roles.filter(r => r.name !== 'admin').map(role => (
            <option key={role._id} value={role._id}>{role.displayName}</option>
          ))}
        </select>
      ),
    },
  ];

  // Sellers columns
  const sellersColumns = [
    {
      key: "uniqueId",
      label: "User ID",
      width: 100,
      render: (value) => <span className="role-serial">{value || "-"}</span>,
    },
    {
      key: "firstName",
      label: "Name",
      width: 180,
      render: (_, row) => (
        <div className="role-user-cell">
          <div className="role-user-avatar">
            {(row.firstName?.[0] || "U").toUpperCase()}
          </div>
          <span className="role-user-name">{row.firstName} {row.lastName}</span>
        </div>
      ),
    },
    {
      key: "email",
      label: "Email",
      width: 220,
      render: (value) => <span className="role-email">{value || "-"}</span>,
    },
    {
      key: "phone",
      label: "Mobile",
      width: 130,
      render: (value) => <span className="role-phone">{value || "-"}</span>,
    },
    {
      key: "roleId",
      label: "Assigned Role",
      width: 140,
      render: (value, row) => (
        <span className={`role-badge ${value?.displayName ? 'role-badge-subadmin' : 'role-badge-no-role'}`}>
          {value?.displayName || "No Role"}
        </span>
      ),
    },
    {
      key: "assignedMenus",
      label: "Assigned Menus",
      width: 130,
      render: (value) => <MenusTooltip menus={value} count={value?.length} />,
    },
    {
      key: "isDisable",
      label: "Status",
      width: 100,
      render: (value) => (
        <span className={`role-status-badge ${value ? 'role-status-inactive' : 'role-status-active'}`}>
          {value ? "Disabled" : "Active"}
        </span>
      ),
    },
    {
      key: "lastLoginDate",
      label: "Last Login",
      width: 130,
      render: (value) => value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "-",
    },
    {
      key: "actions",
      label: "Assign Role",
      width: 160,
      render: (_, row) => (
        <select
          className="role-assign-select"
          value={row.roleId?._id || ""}
          onClick={(e) => e.stopPropagation()}
          onChange={async (e) => {
            if (e.target.value) {
              try {
                await roleService.assignRoleToUser(row._id, e.target.value);
                fetchSellers(sellersPage, sellersLimit, sellersSearch);
                alert("Role assigned successfully! Seller needs to re-login to see updated permissions.");
              } catch (err) {
                alert(err.message || "Failed to assign role");
              }
            } else {
              // Remove role assignment
              try {
                await roleService.assignRoleToUser(row._id, null);
                fetchSellers(sellersPage, sellersLimit, sellersSearch);
                alert("Role removed successfully!");
              } catch (err) {
                alert(err.message || "Failed to remove role");
              }
            }
          }}
        >
          <option value="">No Role</option>
          {roles.filter(r => r.name !== 'admin').map(role => (
            <option key={role._id} value={role._id}>{role.displayName}</option>
          ))}
        </select>
      ),
    },
  ];

  // Toolbar for Roles tab
  const rolesToolbarLeft = (
    <div className="role-toolbar-left">
      <div className="role-search-box">
        <FaSearch className="role-search-icon" />
        <input
          type="text"
          className="role-search-input"
          placeholder="Search roles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
    </div>
  );

  const rolesToolbarRight = (
    <div className="role-toolbar-right">
      <button className="role-refresh-btn" onClick={handleRefresh} title="Refresh">
        <FaSync className={loading ? "role-spin" : ""} />
      </button>
      <button className="role-create-btn" onClick={handleCreateRole}>
        <FaPlus /> Create Role
      </button>
    </div>
  );

  // Toolbar for Sub-Admins tab
  const subAdminsToolbarLeft = (
    <div className="role-toolbar-left">
      <div className="role-search-box">
        <FaSearch className="role-search-icon" />
        <input
          type="text"
          className="role-search-input"
          placeholder="Search sub-admins..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
    </div>
  );

  const subAdminsToolbarRight = (
    <div className="role-toolbar-right">
      <button className="role-refresh-btn" onClick={handleRefresh} title="Refresh">
        <FaSync className={loading ? "role-spin" : ""} />
      </button>
      <button className="role-create-btn" onClick={handleCreateSubAdmin}>
        <FaPlus /> Create Sub-Admin
      </button>
    </div>
  );

  // Toolbar for Sellers tab
  const sellersToolbarLeft = (
    <div className="role-toolbar-left">
      <div className="role-search-box role-search-box-sellers" style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', maxWidth: '600px' }}>
        <FaSearch className="role-search-icon" />
        <input
          type="text"
          className="role-search-input role-search-input-sellers"
          placeholder="Search by name, email, mobile..."
          value={sellersSearch}
          onChange={(e) => setSellersSearch(e.target.value)}
          onKeyPress={handleSellersSearchKeyPress}
          style={{ flex: 1, minWidth: '300px' }}
        />
        <button
          className="role-search-btn"
          onClick={handleSellersSearch}
          style={{
            padding: '6px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            whiteSpace: 'nowrap',
          }}
          title="Search"
        >
          <FaSearch style={{ fontSize: '12px' }} /> Search
        </button>
      </div>
    </div>
  );

  const sellersToolbarRight = (
    <div className="role-toolbar-right">
      <button className="role-refresh-btn" onClick={handleRefresh} title="Refresh">
        <FaSync className={loading ? "role-spin" : ""} />
      </button>
    </div>
  );

  if (loading && roles.length === 0) {
    return (
      <div className="role-page">
        <div className="role-loading">
          <div className="role-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="role-page">
      {/* Tabs */}
      <div className="role-tabs">
        <button
          onClick={() => setActiveTab("roles")}
          className={`role-tab-btn ${activeTab === "roles" ? "role-tab-active" : ""}`}
        >
          <FaKey /> Roles
        </button>
        <button
          onClick={() => setActiveTab("subadmins")}
          className={`role-tab-btn ${activeTab === "subadmins" ? "role-tab-active" : ""}`}
        >
          <FaUserShield /> Sub-Admins
        </button>
        {/* Sellers tab hidden - role assignment now in User Management */}
      </div>

      {/* Roles Tab */}
      {activeTab === "roles" && (
        <div className="role-table-section">
          <ExcelTable
            data={rolesTableData}
            columns={rolesColumns}
            showToolbarExport={true}
            showToolbarFilter={false}
            showToolbarColumns={true}
            showToolbarFullscreen={true}
            toolbarLeftActions={rolesToolbarLeft}
            toolbarRightActions={rolesToolbarRight}
            selectable={false}
            sortable={true}
            resizable={true}
            showToolbar={true}
            title="Roles"
            userId={currentUserId}
          />
        </div>
      )}

      {/* Sub-Admins Tab */}
      {activeTab === "subadmins" && (
        <div className="role-table-section">
          <ExcelTable
            data={subAdminsTableData}
            columns={subAdminsColumns}
            showToolbarExport={true}
            showToolbarFilter={false}
            showToolbarColumns={true}
            showToolbarFullscreen={true}
            toolbarLeftActions={subAdminsToolbarLeft}
            toolbarRightActions={subAdminsToolbarRight}
            selectable={false}
            sortable={true}
            resizable={true}
            showToolbar={true}
            title="Sub-Admins"
            userId={currentUserId}
          />
        </div>
      )}

      {/* Sellers tab removed - role assignment now in User Management */}

      {/* Role Modal */}
      {isRoleModalOpen && (
        <div className="role-modal-overlay" onClick={() => setIsRoleModalOpen(false)}>
          <div className="role-modal-content role-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="role-modal-header">
              <h2>{selectedRole ? "Edit Role" : "Create Role"}</h2>
              <button className="role-modal-close" onClick={() => setIsRoleModalOpen(false)}>&times;</button>
            </div>
            <div className="role-modal-body">
              <div className="role-form-row">
                <div className="role-form-group">
                  <label>Role Name</label>
                  <input
                    type="text"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                    disabled={selectedRole?.isSystem}
                    placeholder="e.g., content-manager"
                  />
                </div>
                <div className="role-form-group">
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={roleForm.displayName}
                    onChange={(e) => setRoleForm({ ...roleForm, displayName: e.target.value })}
                    placeholder="e.g., Content Manager"
                  />
                </div>
              </div>
              <div className="role-form-group">
                <label>Description</label>
                <textarea
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  rows={2}
                  placeholder="Role description..."
                />
              </div>

              <div className="role-form-group">
                <label>Menu Permissions</label>
                <div className="role-permissions-table-wrapper">
                  <table className="role-permissions-table">
                    <thead>
                      <tr>
                        <th>Menu</th>
                        <th>View</th>
                        <th>Create</th>
                        <th>Edit</th>
                        <th>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {menuPermissionRows.map((row) =>
                        row.type === "group" ? (
                          <tr key={row.key} className="role-permissions-group-row">
                            <td colSpan={5}>{row.label}</td>
                          </tr>
                        ) : (
                          <tr key={row.menu.menuKey} className={row.indent ? "role-permissions-sub-row" : ""}>
                            <td>{row.indent ? <span className="role-permissions-sub-label">{row.menu.menuName}</span> : row.menu.menuName}</td>
                            <td>
                              <input
                                type="checkbox"
                                checked={getPermission(row.menu.menuKey, "canView")}
                                onChange={() => toggleMenuPermission(row.menu.menuKey, "canView")}
                              />
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                checked={getPermission(row.menu.menuKey, "canCreate")}
                                onChange={() => toggleMenuPermission(row.menu.menuKey, "canCreate")}
                                disabled={!getPermission(row.menu.menuKey, "canView")}
                              />
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                checked={getPermission(row.menu.menuKey, "canEdit")}
                                onChange={() => toggleMenuPermission(row.menu.menuKey, "canEdit")}
                                disabled={!getPermission(row.menu.menuKey, "canView")}
                              />
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                checked={getPermission(row.menu.menuKey, "canDelete")}
                                onChange={() => toggleMenuPermission(row.menu.menuKey, "canDelete")}
                                disabled={!getPermission(row.menu.menuKey, "canView")}
                              />
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="role-modal-footer">
              <button className="role-btn-secondary" onClick={() => setIsRoleModalOpen(false)}>Cancel</button>
              <button className="role-btn-primary" onClick={handleSaveRole}>
                {selectedRole ? "Update Role" : "Create Role"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Admin Modal */}
      {isSubAdminModalOpen && (
        <div className="role-modal-overlay" onClick={() => setIsSubAdminModalOpen(false)}>
          <div className="role-modal-content" onClick={e => e.stopPropagation()}>
            <div className="role-modal-header">
              <h2>Create Sub-Admin</h2>
              <button className="role-modal-close" onClick={() => setIsSubAdminModalOpen(false)}>&times;</button>
            </div>
            <div className="role-modal-body">
              <div className="role-form-row">
                <div className="role-form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={subAdminForm.firstName}
                    onChange={(e) => setSubAdminForm({ ...subAdminForm, firstName: e.target.value })}
                  />
                </div>
                <div className="role-form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={subAdminForm.lastName}
                    onChange={(e) => setSubAdminForm({ ...subAdminForm, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className="role-form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={subAdminForm.email}
                  onChange={(e) => setSubAdminForm({ ...subAdminForm, email: e.target.value })}
                />
              </div>
              <div className="role-form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={subAdminForm.password}
                  onChange={(e) => setSubAdminForm({ ...subAdminForm, password: e.target.value })}
                />
              </div>
              <div className="role-form-group">
                <label>Assign Role</label>
                <select
                  value={subAdminForm.roleId}
                  onChange={(e) => setSubAdminForm({ ...subAdminForm, roleId: e.target.value })}
                >
                  <option value="">Select Role</option>
                  {roles.filter(r => r.isActive).map((role) => (
                    <option key={role._id} value={role._id}>{role.displayName}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="role-modal-footer">
              <button className="role-btn-secondary" onClick={() => setIsSubAdminModalOpen(false)}>Cancel</button>
              <button className="role-btn-primary" onClick={handleSaveSubAdmin}>Create Sub-Admin</button>
            </div>
          </div>
        </div>
      )}

      {/* Users Modal */}
      {isUsersModalOpen && (
        <div className="role-modal-overlay" onClick={() => setIsUsersModalOpen(false)}>
          <div className="role-modal-content" onClick={e => e.stopPropagation()}>
            <div className="role-modal-header">
              <h2>Users with "{selectedRole?.displayName}" role</h2>
              <button className="role-modal-close" onClick={() => setIsUsersModalOpen(false)}>&times;</button>
            </div>
            <div className="role-modal-body">
              {roleUsers.length === 0 ? (
                <p className="role-no-users">No users assigned to this role</p>
              ) : (
                <div className="role-users-list">
                  {roleUsers.map((user) => (
                    <div key={user._id} className="role-user-item">
                      <div className="role-user-info">
                        <div className="role-user-avatar">{(user.firstName?.[0] || "U").toUpperCase()}</div>
                        <div>
                          <div className="role-user-name">{user.firstName} {user.lastName}</div>
                          <div className="role-user-email">{user.email}</div>
                        </div>
                      </div>
                      <span className={`role-user-status ${user.isDisable ? 'role-user-disabled' : 'role-user-active'}`}>
                        {user.isDisable ? 'Disabled' : 'Active'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="role-modal-footer">
              <button className="role-btn-secondary" onClick={() => setIsUsersModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmModal.isOpen && (
        <div className="role-modal-overlay" onClick={() => setConfirmModal({ isOpen: false, roleId: null })}>
          <div className="role-modal-content role-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="role-modal-header">
              <h2>Delete Role</h2>
              <button className="role-modal-close" onClick={() => setConfirmModal({ isOpen: false, roleId: null })}>&times;</button>
            </div>
            <div className="role-modal-body">
              <p className="role-confirm-message">Are you sure you want to delete this role? This action cannot be undone.</p>
            </div>
            <div className="role-modal-footer">
              <button className="role-btn-secondary" onClick={() => setConfirmModal({ isOpen: false, roleId: null })}>Cancel</button>
              <button className="role-btn-danger" onClick={handleDeleteRole}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
